import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ClipboardList, AlertCircle, Clock, CheckCircle, IndianRupee } from 'lucide-react';
import { ReturnGenerationDashboard as DashboardData } from '@/app/types/compliance';

interface Props {
  data: DashboardData | undefined;
  isLoading: boolean;
}

const ReturnGenerationDashboardComponent: React.FC<Props> = ({ data, isLoading }) => {
  const cards = [
    {
      title: 'Pending',
      value: data?.pending_count ?? 0,
      icon: ClipboardList,
      description: 'Tasks waiting to be filed',
      className: 'text-info',
    },
    {
      title: 'Overdue',
      value: data?.overdue_count ?? 0,
      icon: AlertCircle,
      description: 'Past due date',
      className: 'text-destructive',
    },
    {
      title: 'Due This Week',
      value: data?.due_this_week_count ?? 0,
      icon: Clock,
      description: 'Filing deadline approaching',
      className: 'text-warning',
    },
    {
      title: 'Filed This Month',
      value: data?.filed_this_month_count ?? 0,
      icon: CheckCircle,
      description: 'Successfully filed',
      className: 'text-success',
    },
  ];

  const formatCurrency = (value: string | undefined): string => {
    if (!value) return '0.00';
    const num = parseFloat(value);
    return num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className="space-y-4">
      {/* Status Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {cards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
              <card.icon className={`h-4 w-4 ${card.className}`} />
            </CardHeader>
            <CardContent>
              <div className="text-xl font-semibold tracking-tight">
                {isLoading ? '-' : card.value}
              </div>
              <p className="text-xs text-muted-foreground">{card.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Fee Summary */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Fees</CardTitle>
            <IndianRupee className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-semibold tracking-tight">
              {isLoading ? '-' : `₹${formatCurrency(data?.total_fee_pending)}`}
            </div>
            <p className="text-xs text-muted-foreground">
              Total fees from pending compliance tasks
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Billed Fees</CardTitle>
            <IndianRupee className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-semibold tracking-tight">
              {isLoading ? '-' : `₹${formatCurrency(data?.total_fee_billed)}`}
            </div>
            <p className="text-xs text-muted-foreground">
              Total fees invoiced to clients
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReturnGenerationDashboardComponent;
